import React, { useEffect, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import { useToasts } from "react-toast-notifications";
import Cookies from "js-cookie";
import { ACCESS_TOKEN, SHOP_DATA } from "../constants";
import { SHOP_APP_V2 } from "../graphql/shop/queryShop";
import { SUBSCRIPTION_MESSENGER_COMMENT } from "../graphql/conversation/subscription";
import { emitInboxEvent, onUnreadRefresh, setUnreadCount, useInboxUnread } from "../helpers/inboxEvents";
import { GET_CONVERSATION_UNREAD_COUNT } from "../graphql/conversation/query";
import { onSelectedPageChange, readSelectedPage } from "../helpers/selectedPage";

//* Added by the shop in public/void/.
const NOTIFY_SOUND = `${process.env.PUBLIC_URL || ""}/void/notification.mp3`;
const INBOX_PATH = "/messenger-inbox";
//* Backstop for reads done in another tab or by another staff member.
const UNREAD_POLL_MS = 30000;

/**
 * Customer messages arrive and ring on EVERY screen, not only the inbox.
 *
 * Mounted beside the Navbar, which every logged-in screen renders. Holds the app's one
 * subscription per connected page and republishes each event through helpers/inboxEvents, which
 * the inbox screen and the side-menu badge read. Off the inbox it also shows a toast that opens
 * the inbox, re-counts the unread chats, and puts the count in the tab title so a shop working
 * in another browser tab still sees it.
 */
export default function InboxNotifier({ location, history }) {
    const client = useApolloClient();
    const { addToast } = useToasts();
    const unread = useInboxUnread();
    const onInbox = (location?.pathname || "").startsWith(INBOX_PATH);
    const where = useRef({ onInbox, history });
    where.current = { onInbox, history };
    const audio = useRef(null);
    const lastRing = useRef(0);
    const seen = useRef(new Set());

    const shopId = (() => {
        try { return JSON.parse(localStorage.getItem(SHOP_DATA))?.id || null; } catch (e) { return null; }
    })();
    const loggedIn = !!Cookies.get(ACCESS_TOKEN);

    const ring = () => {
        //* One ring for a burst: ten messages in a second is one notification, not ten.
        if (Date.now() - lastRing.current < 1500) return;
        lastRing.current = Date.now();
        try {
            if (!audio.current) audio.current = new Audio(NOTIFY_SOUND);
            audio.current.currentTime = 0;
            //* Browsers refuse sound until the user has clicked the page once; that is not an error.
            const p = audio.current.play();
            if (p && p.catch) p.catch(() => {});
        } catch (e) { /* no audio support */ }
    };

    useEffect(() => {
        if (!loggedIn || !shopId) return undefined;
        let subs = [];
        let cancelled = false;
        let pageIds = [];
        let timer = null;
        let soon = null;
        //* The badge is the SERVER's count of chats with an unread message, so it survives a
        //* refresh and drops when a chat is opened, not only when the inbox is. It counts the page
        //* picked in chat / all posts (helpers/selectedPage); all pages until one is picked.
        //* Sound and pop-ups still come from every page.
        const countedPages = () => {
            const sel = readSelectedPage();
            return sel && pageIds.includes(sel) ? [sel] : pageIds;
        };
        const recount = async () => {
            if (cancelled || !pageIds.length) return;
            try {
                const { data } = await client.query({
                    query: GET_CONVERSATION_UNREAD_COUNT, variables: { pageIds: countedPages() }, fetchPolicy: "network-only",
                });
                if (!cancelled) setUnreadCount(data?.conversationUnreadCount);
            } catch (e) { /* keep the last count */ }
        };
        //* Coalesce a burst of messages into one count.
        const recountSoon = () => { clearTimeout(soon); soon = setTimeout(recount, 800); };
        const offRefresh = onUnreadRefresh(recountSoon);
        //* Another page picked: count that one now, not on the next poll.
        const offPage = onSelectedPageChange(() => recount());
        (async () => {
            try {
                const { data } = await client.query({
                    query: SHOP_APP_V2, variables: { where: { id: shopId } }, fetchPolicy: "network-only",
                });
                if (cancelled) return;
                const pages = (data?.shop?.pages || []).filter((p) => p?.status);
                pageIds = pages.map((p) => String(p._id));
                recount();
                timer = setInterval(recount, UNREAD_POLL_MS);
                subs = pages.map((page) =>
                    client.subscribe({ query: SUBSCRIPTION_MESSENGER_COMMENT, variables: { pageId: page._id } }).subscribe({
                        next: ({ data: d }) => {
                            const incoming = d?.onMessengerCommentCreated;
                            if (!incoming) return;
                            emitInboxEvent(incoming, page._id);
                            if (incoming.direction === "outbound") return;
                            //* Ring for Messenger chat only; CF comments under posts and lives come through here too.
                            if (incoming.source !== "messenger_dm") return;
                            //* The same message can arrive twice (redelivery); ring and count it once.
                            const key = incoming.id || `${incoming.psid}:${incoming.createdAt}`;
                            if (seen.current.has(key)) return;
                            seen.current.add(key);
                            if (seen.current.size > 500) seen.current = new Set([...seen.current].slice(-200));
                            ring();
                            recountSoon();
                            if (!where.current.onInbox) {
                                const text = String(incoming.message || "").replace(/\s+/g, " ").trim().slice(0, 60);
                                addToast(
                                    <div style={{ cursor: "pointer" }} onClick={() => where.current.history?.push(INBOX_PATH)}>
                                        <b>💬 {incoming.name || "ລູກຄ້າ"}</b> · {page.name}
                                        {text && <div style={{ fontSize: 13 }}>{text}</div>}
                                        <div style={{ fontSize: 12, color: "#888" }}>ກົດເພື່ອເປີດແຊັດ</div>
                                    </div>,
                                    { appearance: "info", autoDismiss: true, autoDismissTimeout: 6000 }
                                );
                            }
                        },
                        error: (e) => console.log("inbox notifier subscription error:", page._id, e?.message || e),
                    }));
            } catch (e) {
                console.log("inbox notifier: could not load pages", e?.message || e);
            }
        })();
        return () => {
            cancelled = true;
            clearInterval(timer);
            clearTimeout(soon);
            offRefresh();
            offPage();
            subs.forEach((s) => s.unsubscribe());
        };
    }, [client, shopId, loggedIn]);

    //* "(3) …" in the tab title while chats are waiting.
    useEffect(() => {
        const base = document.title.replace(/^\(\d+\)\s*/, "");
        document.title = unread > 0 ? `(${unread}) ${base}` : base;
    }, [unread]);

    return null;
}
